/**
 * Client-side file chunking utilities for direct uploads
 */

import type { ProgressCallback } from '@stw/types';

import { stwFetch } from '@/utils/stwFetch';

const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB chunks (matches backend)

interface ChunkUploadUrl {
  index: number;
  url: string;
}

interface ChunkUploadOptions {
  onProgress?: ProgressCallback;
  concurrentUploads?: number;
}

/**
 * Split file into chunks and get signed upload URLs from backend
 * @param itemId - Item ID to upload chunks for
 * @param file - File to chunk and upload
 * @returns Array of chunk upload URLs
 */
const getChunkUploadUrls = async (
  itemId: string,
  file: File
): Promise<ChunkUploadUrl[]> => {
  const chunkCount = Math.ceil(file.size / CHUNK_SIZE);

  const { chunk_urls } = await stwFetch('POST', `/item/${itemId}/chunks/urls`, {
    chunk_count: chunkCount,
    file_size: file.size,
  });

  return chunk_urls;
};

/**
 * Upload a single chunk to GCS using signed URL
 * @param url - Signed upload URL
 * @param chunk - Chunk data as Blob
 * @param index - Chunk index for logging
 */
const uploadChunk = async (
  url: string,
  chunk: Blob,
  index: number
): Promise<void> => {
  // Development mode uses POST with authorization header (GCS emulator)
  if (import.meta.env.DEV) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { Authorization: 'Firebase owner' },
      body: chunk,
    });

    if (!response.ok) {
      throw new Error(
        `Failed to upload chunk ${index}: ${response.status} ${response.statusText}`
      );
    }
    return;
  }

  // Production mode uses PUT
  const response = await fetch(url, {
    method: 'PUT',
    body: chunk,
    headers: {
      'Content-Type': 'application/octet-stream',
    },
  });

  if (!response.ok) {
    throw new Error(
      `Failed to upload chunk ${index}: ${response.status} ${response.statusText}`
    );
  }
};

/**
 * Mark chunk upload as complete on backend
 * @param itemId - Item ID
 */
const markChunksComplete = async (itemId: string): Promise<void> => {
  await stwFetch('POST', `/item/${itemId}/chunks/complete`, {});
};

/**
 * Upload file in chunks with progress tracking
 * @param itemId - Item ID to upload for
 * @param file - File to upload
 * @param options - Upload options (progress callback, concurrency)
 * @returns Promise that resolves when upload is complete
 */
export const uploadFileInChunks = async (
  itemId: string,
  file: File,
  options: ChunkUploadOptions = {}
): Promise<void> => {
  const { onProgress, concurrentUploads = 4 } = options;

  // Get signed URLs for all chunks
  const chunkUrls = await getChunkUploadUrls(itemId, file);
  const totalChunks = chunkUrls.length;

  let uploadedBytes = 0;

  // Upload chunks in batches with concurrency limit
  for (let i = 0; i < totalChunks; i += concurrentUploads) {
    const batchEnd = Math.min(i + concurrentUploads, totalChunks);
    const batchPromises: Promise<void>[] = [];

    for (let j = i; j < batchEnd; j++) {
      const chunkUrl = chunkUrls[j];
      const start = j * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunkBlob = file.slice(start, end);

      const uploadPromise = uploadChunk(
        chunkUrl.url,
        chunkBlob,
        chunkUrl.index
      ).then(() => {
        uploadedBytes += chunkBlob.size;

        if (onProgress) {
          onProgress(uploadedBytes, file.size);
        }
      });

      batchPromises.push(uploadPromise);
    }

    await Promise.all(batchPromises);
  }

  // Mark upload as complete
  await markChunksComplete(itemId);
};
